import React, { useCallback, useRef, useState } from "react";
import { graphql } from "react-relay";

import { faTimes, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import cx from "classnames";

import type { TeamType, UserType } from "~/common/AssigneeFilter";
import { ReactComponent as SearchIcon } from "~/legacy-assets/images/icons/search.svg";
import { RunQuery } from "~/lib/relayUtil";
import { uiAvatar } from "~/lib/utils";
import { Button } from "~/ui";
import UserAvatar from "~/ui/avatar/Avatar";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import type { AssigneeSelectorQuery } from "~/__generated__/AssigneeSelectorQuery.graphql";

import objectiveStyles from "../Objective.module.css";
import styles from "./AssigneeSelector.module.css";

const assigneesQuery = graphql`
	query AssigneeSelectorQuery($query: String) {
		selectUsers: users(first: 15, search: $query, isGuests: false) {
			edges {
				node {
					id
					email
					image
					fullName
					isGuest
				}
			}
		}

		teams(first: 10, query: $query) {
			edges {
				node {
					id
					title
				}
			}
		}
	}
`;

type AssigneeSelectorProps = {
	readOnly?: boolean;
	user: UserType | null;
	team: TeamType | null;
	avatarOnly?: boolean;
	setUser: (u: UserType) => void;
	setTeam: (t: TeamType) => void;
};

export default function AssigneeSelector({
	user,
	team,
	setUser,
	setTeam,
	readOnly,
	avatarOnly,
}: AssigneeSelectorProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [usersList, setUsersList] = useState<UserType[]>([]);
	const [teamsList, setTeamsList] = useState<TeamType[]>([]);
	const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
	const searchInputRef = useRef<HTMLInputElement>(null);

	const onQueryLoading = useCallback(() => {
		setLoading(true);
	}, [setLoading]);

	const onQueryResponse = useCallback((result: AssigneeSelectorQuery["response"]) => {
		setLoading(false);
		if (result.selectUsers?.edges) setUsersList(result.selectUsers.edges.map(e => e.node));
		if (result.teams?.edges) setTeamsList(result.teams.edges.map(e => e.node));
	}, []);

	const onClear = useCallback(() => setSearch(""), [setSearch]);

	const onInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setSearch(event.target.value);
		},
		[setSearch],
	);

	const selectUser = useCallback(
		(user: UserType) => () => {
			setUser(user);
			setIsOpen(false);
		},
		[setUser],
	);

	const selectTeam = useCallback(
		(team: TeamType) => () => {
			setTeam(team);
			setIsOpen(false);
		},
		[setTeam],
	);

	const onFocusSearch = useCallback(() => {
		setIsSearchFocused(true);
	}, []);

	const onBlurSearch = useCallback(() => {
		setIsSearchFocused(false);
	}, []);

	let content = (
		<>
			<RunQuery<AssigneeSelectorQuery>
				query={assigneesQuery}
				variables={{ query: search }}
				fetchPolicy="store-or-network"
				onLoading={onQueryLoading}
				onResult={onQueryResponse}
			/>

			<div className={styles.searchInput}>
				<div className={cx(styles.searchIcon, { loading })}>
					{loading ? (
						<LoadingSpinner inline />
					) : (
						<SearchIcon className={cx(styles.searchIcon, { [styles.searchIconDark]: isSearchFocused })} />
					)}
				</div>
				<input
					ref={searchInputRef}
					value={search}
					onChange={onInputChange}
					placeholder="Search users"
					onFocus={onFocusSearch}
					onBlur={onBlurSearch}
					name="assignee_search"
				/>
				{search.length > 0 ? (
					<a href="#" onClick={onClear} className="clear-button">
						<FontAwesomeIcon icon={faTimes} />
					</a>
				) : null}
			</div>
			<div className={styles.searchWrapper}>
				{teamsList.map(t => (
					<div className={styles.searchUserRow} key={`team-${t.id}`} onClick={selectTeam(t)}>
						<UserAvatar user={{ fullName: t.title, image: uiAvatar(faUsers) }} />

						<span>{t.title}</span>
					</div>
				))}
				{usersList.map(u => (
					<div className={styles.searchUserRow} key={`user-${u.id}`} onClick={selectUser(u)}>
						<UserAvatar user={u} />

						<span>{u.fullName}</span>
					</div>
				))}
			</div>
		</>
	);

	return (
		<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
			<Popover.Trigger asChild>
				<Button
					size="wide"
					className={cx(objectiveStyles.dataButton, { [objectiveStyles.dataButtonDisabled]: readOnly })}
				>
					{team ? (
						<>
							<UserAvatar user={{ fullName: team.title, image: uiAvatar(faUsers) }} />
							{!avatarOnly && <span className={objectiveStyles.assignedToName}>{team.title}</span>}
						</>
					) : user ? (
						<>
							<UserAvatar user={user} />
							{!avatarOnly && <span className={objectiveStyles.assignedToName}>{user.fullName}</span>}
						</>
					) : (
						<>
							<FontAwesomeIcon icon={faUser} />
							{!avatarOnly && <span className={objectiveStyles.assignedToName}>Assign to...</span>}
						</>
					)}
				</Button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content side="bottom" align="center" sideOffset={9} className={styles.popover}>
					{content}
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}
